/**
 * Food Profiles - Educational information about whole foods
 * commonly discussed in integrative oncology and patient communities
 */

export const foodProfiles = [
  // LEAFY GREENS
  {
    id: 'kale',
    name: 'Kale',
    category: 'Leafy Greens',
    whyIncluded: 'Traditionally valued for its nutrient density and often discussed in plant-forward eating patterns. Common in whole-food, anti-inflammatory dietary approaches.',
    keyNutrients: [
      'Vitamin K',
      'Vitamin A',
      'Vitamin C',
      'Folate',
      'Calcium',
      'Antioxidants',
      'Fiber'
    ],
    preparation: 'Often steamed or lightly cooked to improve digestibility. Some people choose to massage raw kale with a small amount of oil. Cooking reduces bitterness and may improve nutrient absorption.',
    cautions: 'May not be suitable for everyone. Some people moderate intake if taking certain medications. Discuss with your care team if you have concerns about vitamin K intake.'
  },
  {
    id: 'spinach',
    name: 'Spinach',
    category: 'Leafy Greens',
    whyIncluded: 'Often discussed for its nutrient density and versatility. Common in macrobiotic-style and Mediterranean dietary patterns.',
    keyNutrients: [
      'Folate',
      'Iron',
      'Vitamin K',
      'Vitamin A',
      'Magnesium',
      'Antioxidants'
    ],
    preparation: 'Can be consumed lightly cooked or raw. Cooking may increase iron availability while reducing oxalate content. Some people prefer steaming over boiling to preserve nutrients.',
    cautions: 'May not be suitable for everyone. Contains oxalates; some people moderate raw spinach consumption. Discuss with your healthcare provider if you have specific dietary restrictions.'
  },
  {
    id: 'watercress',
    name: 'Watercress',
    category: 'Leafy Greens',
    whyIncluded: 'Traditionally valued in whole-food dietary approaches. Often discussed for its peppery flavor and nutrient content.',
    keyNutrients: [
      'Vitamin K',
      'Vitamin C',
      'Calcium',
      'Antioxidants',
      'Folate'
    ],
    preparation: 'Often consumed raw in salads or lightly wilted. Best used fresh. Some people add it to soups near the end of cooking.',
    cautions: 'May not be suitable for everyone. Wash thoroughly before consumption. Discuss with your care team if you have concerns about vitamin K intake.'
  },

  // LEGUMES
  {
    id: 'lentils',
    name: 'Lentils',
    category: 'Legumes',
    whyIncluded: 'Common in macrobiotic and plant-based dietary patterns. Often discussed for protein and fiber content.',
    keyNutrients: [
      'Protein',
      'Folate',
      'Iron',
      'Fiber',
      'Magnesium',
      'B vitamins'
    ],
    preparation: 'Do not require pre-soaking. Cook thoroughly until soft. Some people find red lentils easier to digest than green or brown varieties. Can be made into soups, stews, or purées.',
    cautions: 'May not be suitable for everyone. Some people experience digestive discomfort. Start with small portions if new to legumes.'
  },
  {
    id: 'chickpeas',
    name: 'Chickpeas (Garbanzo Beans)',
    category: 'Legumes',
    whyIncluded: 'Traditionally valued in Mediterranean and Middle Eastern dietary patterns. Often discussed for protein and fiber content.',
    keyNutrients: [
      'Protein',
      'Fiber',
      'Folate',
      'Iron',
      'Magnesium',
      'B vitamins'
    ],
    preparation: 'Best soaked overnight before cooking. Cook thoroughly until soft. Can be roasted, puréed (hummus), or added to soups. Some people remove skins for easier digestion.',
    cautions: 'May not be suitable for everyone. Some people experience digestive discomfort. Discuss with your healthcare provider if you have specific digestive concerns.'
  },
  {
    id: 'adzuki-beans',
    name: 'Adzuki Beans',
    category: 'Legumes',
    whyIncluded: 'Common in macrobiotic and Asian dietary traditions. Often discussed for their sweet flavor and nutrient content.',
    keyNutrients: [
      'Protein',
      'Fiber',
      'Folate',
      'Iron',
      'Magnesium',
      'Antioxidants'
    ],
    preparation: 'Best soaked overnight before cooking. Cook thoroughly until tender. Can be made into sweet or savory dishes. Often cooked with kombu seaweed in macrobiotic preparation.',
    cautions: 'May not be suitable for everyone. Some people experience digestive discomfort. Start with small portions if new to this legume.'
  },

  // WHOLE GRAINS
  {
    id: 'brown-rice',
    name: 'Brown Rice',
    category: 'Whole Grains',
    whyIncluded: 'Central to macrobiotic dietary approaches. Often discussed as a staple whole grain for daily consumption.',
    keyNutrients: [
      'Fiber',
      'B vitamins',
      'Magnesium',
      'Selenium',
      'Manganese'
    ],
    preparation: 'Rinse before cooking. Some people soak for several hours to improve digestibility. Pressure cooking or slow cooking preferred in macrobiotic preparation. Can be combined with other grains.',
    cautions: 'May not be suitable for everyone. Some people moderate portion sizes. Individual tolerance varies.'
  },
  {
    id: 'millet',
    name: 'Millet',
    category: 'Whole Grains',
    whyIncluded: 'Common in macrobiotic and traditional dietary patterns. Often discussed as an alkaline-forming grain.',
    keyNutrients: [
      'Magnesium',
      'Fiber',
      'B vitamins',
      'Iron',
      'Antioxidants'
    ],
    preparation: 'Rinse before cooking. Can be toasted before boiling for enhanced flavor. Cooks to a fluffy or creamy consistency depending on water ratio. Some people combine with other grains.',
    cautions: 'May not be suitable for everyone. Generally well-tolerated. Discuss with your healthcare provider if you have specific dietary restrictions.'
  },
  {
    id: 'buckwheat',
    name: 'Buckwheat',
    category: 'Whole Grains',
    whyIncluded: 'Common in macrobiotic dietary approaches. Often discussed as a warming grain. Not actually wheat (gluten-free seed).',
    keyNutrients: [
      'Protein',
      'Fiber',
      'Magnesium',
      'Manganese',
      'Antioxidants'
    ],
    preparation: 'Can be toasted (kasha) or used raw. Cooks quickly compared to other grains. Can be made into porridge or added to soups. Often made into soba noodles.',
    cautions: 'May not be suitable for everyone. Some people are allergic to buckwheat. Introduce gradually if new to this food.'
  },

  // FERMENTED FOODS
  {
    id: 'miso',
    name: 'Miso',
    category: 'Fermented Foods',
    whyIncluded: 'Central to macrobiotic dietary tradition. Often discussed for its fermented properties and use in daily soup.',
    keyNutrients: [
      'Protein',
      'B vitamins',
      'Probiotics',
      'Minerals',
      'Enzymes'
    ],
    preparation: 'Use unpasteurized miso when possible. Add to soups at the end of cooking; do not boil. Start with lighter varieties (white/yellow) before darker ones. Store refrigerated.',
    cautions: 'May not be suitable for everyone. Contains sodium; some people moderate intake. Unpasteurized varieties may not be appropriate if immunocompromised. Discuss with your care team.'
  },
  {
    id: 'sauerkraut',
    name: 'Sauerkraut',
    category: 'Fermented Foods',
    whyIncluded: 'Traditionally valued in European dietary patterns. Often discussed for its fermented properties.',
    keyNutrients: [
      'Vitamin C',
      'Vitamin K',
      'Fiber',
      'Probiotics',
      'Enzymes'
    ],
    preparation: 'Look for raw, unpasteurized varieties for probiotic benefits. Start with small amounts (1-2 tablespoons). Can be consumed as a condiment. Store refrigerated.',
    cautions: 'May not be suitable for everyone. Unpasteurized fermented foods may not be appropriate if immunocompromised. Contains sodium. Discuss with your healthcare provider before consuming.'
  },

  // SEEDS & NUTS
  {
    id: 'pumpkin-seeds',
    name: 'Pumpkin Seeds',
    category: 'Seeds & Nuts',
    whyIncluded: 'Often discussed in integrative nutrition for their nutrient density. Common in plant-forward dietary patterns.',
    keyNutrients: [
      'Magnesium',
      'Zinc',
      'Iron',
      'Protein',
      'Healthy fats',
      'Fiber'
    ],
    preparation: 'Can be consumed raw or lightly toasted. Some people soak overnight to improve digestibility. Can be ground and added to foods. Store in a cool, dark place.',
    cautions: 'May not be suitable for everyone. Some people moderate intake due to texture or digestive considerations. Individual tolerance varies.'
  },
  {
    id: 'sesame-seeds',
    name: 'Sesame Seeds',
    category: 'Seeds & Nuts',
    whyIncluded: 'Common in macrobiotic dietary approaches. Often discussed for mineral content, particularly calcium.',
    keyNutrients: [
      'Calcium',
      'Magnesium',
      'Iron',
      'Healthy fats',
      'Protein',
      'Fiber'
    ],
    preparation: 'Lightly toast to enhance flavor and digestibility. Grind into paste (tahini) or sprinkle whole. Black sesame seeds often preferred in macrobiotic preparation. Can be made into gomasio (sesame salt).',
    cautions: 'May not be suitable for everyone. Some people are allergic to sesame. Individual tolerance varies.'
  },
  {
    id: 'almonds',
    name: 'Almonds',
    category: 'Seeds & Nuts',
    whyIncluded: 'Often discussed in plant-forward dietary patterns for their nutrient density.',
    keyNutrients: [
      'Vitamin E',
      'Magnesium',
      'Protein',
      'Healthy fats',
      'Fiber',
      'Calcium'
    ],
    preparation: 'Some people soak overnight to improve digestibility and reduce phytic acid. Can be consumed whole, sliced, or ground into butter. Best stored in refrigerator to prevent rancidity.',
    cautions: 'May not be suitable for everyone. Some people are allergic to tree nuts. High in calories; some people moderate portion sizes. Individual tolerance varies.'
  },
  {
    id: 'flax-seeds',
    name: 'Flax Seeds',
    category: 'Seeds & Nuts',
    whyIncluded: 'Often discussed for their omega-3 content. Common in plant-based dietary approaches.',
    keyNutrients: [
      'Omega-3 fatty acids',
      'Fiber',
      'Protein',
      'Magnesium',
      'Lignans'
    ],
    preparation: 'Must be ground for nutrient absorption; whole seeds pass through undigested. Grind fresh as needed or store ground flax in refrigerator. Can be added to smoothies, oatmeal, or baked goods.',
    cautions: 'May not be suitable for everyone. Some people moderate intake due to fiber content. Store properly to prevent rancidity. Discuss with your healthcare provider if you have hormone-sensitive conditions.'
  },

  // VEGETABLES
  {
    id: 'broccoli',
    name: 'Broccoli',
    category: 'Vegetables',
    whyIncluded: 'Often discussed in integrative nutrition for its nutrient content. Common in anti-inflammatory dietary patterns.',
    keyNutrients: [
      'Vitamin C',
      'Vitamin K',
      'Folate',
      'Fiber',
      'Antioxidants',
      'Sulforaphane'
    ],
    preparation: 'Often lightly steamed to preserve nutrients while improving digestibility. Some people consume raw in small amounts. Can be roasted or added to soups. Avoid overcooking.',
    cautions: 'May not be suitable for everyone. Some people experience digestive discomfort. Those taking blood thinners should discuss vitamin K intake with their healthcare provider.'
  },
  {
    id: 'carrots',
    name: 'Carrots',
    category: 'Vegetables',
    whyIncluded: 'Common in macrobiotic and traditional dietary patterns. Often discussed for their versatility and sweet flavor.',
    keyNutrients: [
      'Vitamin A (beta-carotene)',
      'Fiber',
      'Vitamin K',
      'Potassium',
      'Antioxidants'
    ],
    preparation: 'Can be consumed raw or cooked. Cooking may increase availability of certain nutrients. Often used in soups, stews, or as a side dish. Can be juiced (some people dilute carrot juice).',
    cautions: 'May not be suitable for everyone. Individual tolerance varies. Some people moderate juice intake due to natural sugar content.'
  },
  {
    id: 'sweet-potato',
    name: 'Sweet Potato',
    category: 'Vegetables',
    whyIncluded: 'Common in macrobiotic and traditional dietary approaches. Often discussed for its naturally sweet flavor and nutrient content.',
    keyNutrients: [
      'Vitamin A (beta-carotene)',
      'Fiber',
      'Vitamin C',
      'Potassium',
      'Manganese',
      'B vitamins'
    ],
    preparation: 'Often baked, steamed, or pressure-cooked. Can be mashed or cubed. Some people prefer Japanese varieties in macrobiotic preparation. Skin is edible and nutrient-rich.',
    cautions: 'May not be suitable for everyone. Individual tolerance varies. Some people moderate portion sizes.'
  },
  {
    id: 'daikon-radish',
    name: 'Daikon Radish',
    category: 'Vegetables',
    whyIncluded: 'Common in macrobiotic and Asian dietary traditions. Often discussed as a digestive aid when grated fresh.',
    keyNutrients: [
      'Vitamin C',
      'Fiber',
      'Potassium',
      'Enzymes',
      'Antioxidants'
    ],
    preparation: 'Can be consumed raw (grated) or cooked. Fresh grated daikon often served with heavier meals. Can be pickled, added to soups, or dried. Cooking mellows the sharp flavor.',
    cautions: 'May not be suitable for everyone. Raw daikon is pungent; start with small amounts. Individual tolerance varies.'
  },

  // SEA VEGETABLES
  {
    id: 'seaweed',
    name: 'Seaweed (Wakame / Nori)',
    category: 'Sea Vegetables',
    whyIncluded: 'Central to macrobiotic dietary tradition. Often discussed for mineral content, particularly iodine.',
    keyNutrients: [
      'Iodine',
      'Iron',
      'Calcium',
      'Magnesium',
      'B vitamins',
      'Fiber'
    ],
    preparation: 'Wakame: rehydrate and add to miso soup or salads. Nori: commonly used for wrapping. Rinse well before use. Start with small amounts. Can be added to grains while cooking.',
    cautions: 'May not be suitable for everyone. High in iodine; some people need to moderate intake. Discuss with your healthcare provider if you have thyroid conditions or are taking medications that interact with iodine.'
  },

  // TEAS & BEVERAGES
  {
    id: 'green-tea',
    name: 'Green Tea (Sencha / Matcha)',
    category: 'Teas & Beverages',
    whyIncluded: 'Often discussed in integrative approaches for its antioxidant content. Common in Asian dietary traditions.',
    keyNutrients: [
      'Antioxidants (catechins)',
      'L-theanine',
      'Small amounts of vitamins and minerals'
    ],
    preparation: 'Brew with water below boiling (70-80°C / 160-175°F) to avoid bitterness. Steep 1-3 minutes. Some people prefer sencha for daily use, matcha for occasional consumption. Avoid on empty stomach if sensitive.',
    cautions: 'May not be suitable for everyone. Contains caffeine; some people moderate intake or avoid in evening. May interact with certain medications. Discuss with your healthcare provider if you take blood thinners or have iron concerns.'
  },
  {
    id: 'turmeric-tea',
    name: 'Turmeric Tea',
    category: 'Teas & Beverages',
    whyIncluded: 'Often discussed in integrative nutrition. Traditional in Ayurvedic and some Asian dietary patterns.',
    keyNutrients: [
      'Curcumin',
      'Antioxidants',
      'Small amounts of minerals'
    ],
    preparation: 'Can be made with fresh or dried turmeric root. Often combined with black pepper to improve absorption. Some people add ginger or honey. Can be made as a simple tea or golden milk.',
    cautions: 'May not be suitable for everyone. High doses may cause digestive discomfort in some people. May interact with certain medications. Discuss with your healthcare provider before regular consumption, especially if taking blood thinners.'
  }
];

export const categories = [
  'Leafy Greens',
  'Legumes',
  'Whole Grains',
  'Fermented Foods',
  'Seeds & Nuts',
  'Vegetables',
  'Sea Vegetables',
  'Teas & Beverages'
];
